import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, FileSpreadsheet, Loader2, Plus, RefreshCw, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { ApiError, getApiErrorMessage } from "@/lib/api";
import { InfoHint } from "@/components/admin/InfoHint";
import {
  applySimulation,
  approveVersion,
  confirmImport,
  createDraft,
  discardDraft,
  downloadSimulationTemplate,
  fetchVersion,
  fetchVersions,
  previewImportEnriched,
  reviseVersion,
  simulateVersion,
  type ImportPreviewEnrichedResponse,
  type ProfitabilityRow,
  type SimOverride,
  type SimulationResponse,
  type SimulationRow,
  type VersionDetail,
  type VersionSummary,
} from "@/lib/adminNegotiationPriceTableApi";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const money = (v: number | null | undefined) => (v == null ? "—" : brl.format(v));
// Margem em formato brasileiro com 2 casas; a classificação (ACIMA_META/ENTRE/ABAIXO_MINIMA) e a precisão
// vêm do backend — aqui é só apresentação. Ausente → "—" (nunca 0 nem NaN).
const marginFmt = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const marginPct = (v: number | null | undefined) => (v == null ? "—" : `${marginFmt.format(Number(v))}%`);
// Variação do preço de venda com sinal explícito (+ para aumento). Ausente → "—".
const changePct = (v: number | null | undefined) =>
  v == null ? "—" : `${v > 0 ? "+" : ""}${marginFmt.format(Number(v))}%`;
// Data ISO (YYYY-MM-DD) em pt-BR. Ausente → "—".
const brDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = iso.slice(0, 10).split("-");
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : iso;
};

const marginStatusStyle: Record<string, string> = {
  ACIMA_META: "text-primary",
  ENTRE: "text-foreground",
  ABAIXO_MINIMA: "text-destructive",
};

const scopeLabel = (scope: string | null | undefined) =>
  scope === "UNIDADE_ENTREGA" ? "Unidade de entrega" : scope === "CLIENTE" ? "Cliente" : (scope ?? "—");

// Detalhamento dos componentes de custo conhecidos (tooltip). Componente ausente é omitido — nunca vira
// zero. Todos os valores vêm do backend.
function costBreakdown(r: ProfitabilityRow): string {
  const parts: string[] = [];
  if (r.rawCost != null) parts.push(`Matéria-prima: ${money(r.rawCost)}${r.rawCostUnitName ? ` / ${r.rawCostUnitName}` : ""}`);
  if (r.mermaSurcharge != null) parts.push(`Merma: ${money(r.mermaSurcharge)}`);
  if (r.operationalCost != null) parts.push(`Operacional: ${money(r.operationalCost)}`);
  if (r.packagingCost != null) parts.push(`Embalagem: ${money(r.packagingCost)}`);
  return parts.join("\n");
}

const statusBadge: Record<string, string> = {
  DRAFT: "bg-warning/15 text-warning",
  APPROVED: "bg-primary/10 text-primary",
  SUPERSEDED: "bg-secondary text-muted-foreground",
};
const statusLabel: Record<string, string> = {
  DRAFT: "Rascunho", APPROVED: "Aprovada", SUPERSEDED: "Substituída",
};

/**
 * Tabela de preço VERSIONADA vinculada a uma negociação (Opção B). Rascunho editável/importável; ao
 * aprovar, congela os preços próprios + herdados. Revisar uma aprovada cria nova versão (nova aprovação).
 * Editar/importar aqui NÃO altera a tabela vigente — só a versão desta negociação.
 */
export function NegotiationPriceTablePanel({ conditionId, canEdit }: { conditionId: string; canEdit: boolean }) {
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openVersionId, setOpenVersionId] = useState<string | null>(null);
  const [detail, setDetail] = useState<VersionDetail | null>(null);
  const [preview, setPreview] = useState<ImportPreviewEnrichedResponse | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVersions(await fetchVersions(conditionId));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar as versões de tabela."));
    } finally {
      setLoading(false);
    }
  }, [conditionId]);

  useEffect(() => { void load(); }, [load]);

  const openDetail = useCallback(async (versionId: string) => {
    if (openVersionId === versionId) { setOpenVersionId(null); setDetail(null); return; }
    setOpenVersionId(versionId);
    setDetail(null);
    try {
      setDetail(await fetchVersion(conditionId, versionId));
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar a versão."));
    }
  }, [conditionId, openVersionId]);

  const guard = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Não foi possível concluir a operação."));
    } finally {
      setBusy(false);
    }
  };

  const draft = versions.find((v) => v.status === "DRAFT");
  const approved = versions.find((v) => v.status === "APPROVED");

  const onPickFile = async (versionId: string, file: File | null) => {
    if (!file) return;
    setPendingFile(file);
    setConfirmError(null);
    setBusy(true);
    try {
      setPreview(await previewImportEnriched(conditionId, versionId, file));
    } catch (e) {
      // Preserva o arquivo escolhido; informa o motivo (modelo antigo, outra negociação, etc.) sem descartar.
      setPreview(null);
      setConfirmError(getApiErrorMessage(e, "Falha na prévia da importação."));
      toast.error(getApiErrorMessage(e, "Falha na prévia da importação."));
    } finally {
      setBusy(false);
    }
  };

  const confirmPreview = async (versionId: string, expectedVersion: number) => {
    if (!pendingFile) return;
    setConfirmError(null);
    setBusy(true);
    try {
      await confirmImport(conditionId, versionId, pendingFile, expectedVersion);
      setPreview(null);
      setPendingFile(null);
      toast.success("Preços negociados aplicados ao rascunho.");
      if (openVersionId === versionId) setDetail(await fetchVersion(conditionId, versionId));
      await load();
    } catch (e) {
      // Conflito (base mudou, concorrência, outra negociação): mantém arquivo + prévia e mostra o motivo.
      setConfirmError(getApiErrorMessage(e, "Não foi possível aplicar a importação."));
      toast.error(getApiErrorMessage(e, "Não foi possível aplicar a importação."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Carregando tabela…</div>;
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-dashed border-border/70 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-semibold">Tabela de preços da negociação</span>
        {canEdit && (
          <span className="ml-auto flex flex-wrap gap-1">
            {!draft && (
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => void guard(() => createDraft(conditionId), "Rascunho criado.")}>
                <Plus className="mr-1 h-3.5 w-3.5" /> Novo rascunho
              </Button>
            )}
            {!draft && approved && (
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => void guard(() => reviseVersion(conditionId, approved.id), "Revisão criada (novo rascunho).")}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Revisar aprovada
              </Button>
            )}
          </span>
        )}
      </div>

      {versions.length === 0 && (
        <div className="text-xs text-muted-foreground">
          Nenhuma versão de tabela. Crie um rascunho para preparar os preços desta negociação.
        </div>
      )}

      <div className="space-y-1.5">
        {versions.map((v) => (
          <div key={v.id} className="rounded border border-border p-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase", statusBadge[v.status])}>
                {statusLabel[v.status]}
              </span>
              <span className="font-semibold">v{v.versionNumber}</span>
              <span className="text-muted-foreground">
                {v.ownEntryCount} próprio(s) · {v.inheritedEntryCount} herdado(s) · origem {v.source}
              </span>
              {v.approvedAt && (
                <span className="text-muted-foreground">aprovada por {v.approvedBy ?? "—"}</span>
              )}
              <span className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => void openDetail(v.id)}>
                  {openVersionId === v.id ? "Ocultar" : "Ver"}
                </Button>
                {canEdit && v.status === "DRAFT" && (
                  <>
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => void downloadSimulationTemplate(conditionId, v.id)}>
                      <Download className="mr-1 h-3.5 w-3.5" /> Baixar modelo XLSX
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
                      <Upload className="mr-1 h-3.5 w-3.5" /> Importar XLSX
                    </Button>
                    <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                      onChange={(e) => { void onPickFile(v.id, e.target.files?.[0] ?? null); e.target.value = ""; }} />
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => void guard(() => approveVersion(conditionId, v.id, v.version), "Programação aprovada e congelada.")}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Aprovar
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy}
                      onClick={() => void guard(() => discardDraft(conditionId, v.id), "Rascunho descartado.")}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </>
                )}
              </span>
            </div>

            {openVersionId === v.id && (
              <div className="mt-2 border-t border-border/60 pt-2">
                {!detail && <div className="text-muted-foreground">Carregando…</div>}
                {detail && detail.entries.length === 0 && (
                  <div className="text-muted-foreground">Sem entradas. Importe um XLSX ou use o modelo.</div>
                )}
                {detail && detail.entries.length > 0 && (
                  <div className="max-h-64 overflow-auto">
                    <table className="w-full text-[11px]">
                      <thead className="text-muted-foreground">
                        <tr className="text-left">
                          <th className="py-1 pr-2">Produto</th>
                          <th className="py-1 pr-2">Unid.</th>
                          <th className="py-1 pr-2">Corte</th>
                          <th className="py-1 pr-2 text-right">Preço</th>
                          <th className="py-1">Origem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.entries.map((e, i) => (
                          <tr key={i} className="border-t border-border/40">
                            <td className="py-1 pr-2">{e.productCode ? `${e.productCode} · ` : ""}{e.productName}</td>
                            <td className="py-1 pr-2">{e.unitName}</td>
                            <td className="py-1 pr-2">{e.cutName ?? "base"}</td>
                            <td className="py-1 pr-2 text-right tabular-nums">{money(e.price)}</td>
                            <td className="py-1">
                              <span className={cn("rounded px-1 text-[9px] font-bold uppercase",
                                e.origin === "OWN" ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground")}>
                                {e.origin === "OWN" ? "próprio" : "herdado"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {detail && (
                  <SimulationView
                    key={v.id}
                    conditionId={conditionId}
                    versionId={v.id}
                    onApplied={async () => {
                      if (openVersionId === v.id) setDetail(await fetchVersion(conditionId, v.id));
                      await load();
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {confirmError && !preview && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
          {confirmError} O arquivo selecionado foi preservado — baixe a planilha atualizada e tente novamente.
        </div>
      )}

      {preview && draft && (
        <div className="rounded-md border border-border bg-muted/30 p-2 text-xs">
          <div className="mb-1 flex flex-wrap items-center gap-2 font-semibold">
            Prévia (16 colunas): {preview.fileName}
            <span className="text-muted-foreground">
              {preview.alteredCount} alterada(s) · {preview.keptCount} mantida(s) · {preview.errorCount} erro(s) · {preview.duplicateCount} duplicada(s)
            </span>
          </div>
          {preview.baseChanged && (
            <div className="mb-2 rounded border border-warning/40 bg-warning/10 p-1.5 text-warning">
              A referência desta negociação mudou desde o download. Baixe a planilha atualizada e confira novamente antes de aplicar.
            </div>
          )}

          {/* Erros/duplicidades por linha do arquivo (impedem a confirmação) */}
          {preview.fileRows.some((r) => r.status === "ERRO" || r.status === "DUPLICADA") && (
            <div className="mb-2 rounded border border-destructive/40 bg-destructive/5 p-1.5 text-destructive">
              <div className="mb-0.5 font-semibold">Linhas com problema:</div>
              <ul className="list-inside list-disc">
                {preview.fileRows.filter((r) => r.status === "ERRO" || r.status === "DUPLICADA").map((r) => (
                  <li key={r.lineNumber}>
                    Linha {r.lineNumber} · {r.productCode ?? "—"} {r.productName ?? "—"} — <span className="font-medium">{r.status}</span>: {r.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tabela de 16 colunas recalculada pelo backend com os preços do arquivo */}
          <SimContextHeader data={preview.simulation} />
          <SimTable data={preview.simulation} readOnly />

          {confirmError && (
            <div className="mt-2 rounded border border-destructive/40 bg-destructive/5 p-1.5 text-destructive">{confirmError}</div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={busy || !preview.confirmable}
              onClick={() => void confirmPreview(draft.id, draft.version)}>
              Confirmar importação
            </Button>
            <Button size="sm" variant="ghost" disabled={busy}
              onClick={() => { setPreview(null); setPendingFile(null); setConfirmError(null); }}>Cancelar</Button>
            {!preview.confirmable && !preview.baseChanged && preview.alteredCount === 0 && preview.errorCount === 0 && preview.duplicateCount === 0 && (
              <span className="text-muted-foreground">Nenhum preço negociado preenchido — nada a aplicar.</span>
            )}
            {!preview.confirmable && (preview.errorCount > 0 || preview.duplicateCount > 0) && (
              <span className="text-destructive">Corrija os erros/duplicidades no arquivo antes de confirmar.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Chave estável de uma linha (produto + unidade + corte) para casar input ↔ linha e montar overrides.
const rowKey = (r: { productId: string; unitId: string; cutName: string | null }) =>
  `${r.productId}|${r.unitId}|${r.cutName ?? ""}`;

// Cabeçalho de coluna com tooltip explicativo acessível (mouse/teclado/toque via InfoHint/Popover).
function Th({ label, hint, right, cls }: { label: string; hint?: string; right?: boolean; cls?: string }) {
  return (
    <th className={cn("py-1 px-1.5 align-bottom whitespace-nowrap", right ? "text-right" : "text-left", cls)}>
      {label}
      {hint && <InfoHint label={label}>{hint}</InfoHint>}
    </th>
  );
}

/**
 * Tabela de 16 colunas reutilizável (visão ao vivo + prévia de importação). Renderiza a resposta de
 * simulação como veio do backend — nenhum cálculo financeiro no FE. Em modo leitura (`readOnly`) a coluna
 * "Novo preço" apenas exibe o valor recalculado; no modo editável recebe o input + handlers.
 */
function SimTable({
  data, inputs, dirty, onInput, readOnly,
}: {
  data: SimulationResponse;
  inputs?: Record<string, string>;
  dirty?: boolean;
  onInput?: (key: string, value: string) => void;
  readOnly?: boolean;
}) {
  const fin = data.financialVisible;
  const editable = !readOnly && data.editable;
  // Nº de colunas (colSpan do estado vazio). Base 1–4,9,10,11,14,16 = 9; financeiras 5–8,12,13,15 = 7.
  const colCount = 9 + (fin ? 7 : 0);
  return (
    <div className="max-h-[28rem] overflow-auto">
      <table className="w-full text-[11px]">
        <thead className="sticky top-0 bg-background text-muted-foreground">
          <tr className="border-b border-border">
            <Th label="Código" hint="Código do produto no catálogo." />
            <Th label="Produto" hint="Nome do produto." />
            <Th label="Unidade" hint="Unidade comercial da apresentação." />
            <Th label="Corte" hint="Corte da apresentação; “base” quando não há corte específico." />
            {fin && <Th right label="Custo" hint="Custo composto da apresentação (matéria-prima, merma, operacional, embalagem). “parcial” quando faltam componentes; nunca somado como zero." />}
            {fin && <Th right label="Comissão do cliente" hint="Comissão do cliente da negociação, aplicada sobre o preço de venda." />}
            {fin && <Th right label="Comissão do intermediário" hint="Comissão do intermediário, sobre o preço de venda ou após a comissão do cliente, conforme a negociação." />}
            {fin && <Th right label="Impostos" hint="Impostos/despesas percentuais e fixas por produto da negociação." />}
            <Th right label="Preço cadastrado" hint="Valor da tabela antes do piso (próprio ou herdado). Informativo." />
            <Th label="Ajuste de piso" hint="Indica quando o preço de venda subiu em relação ao cadastrado por causa do piso do produto." />
            <Th right label="Preço de venda" hint="Valor final cobrado, após as regras oficiais, inclusive o piso." />
            {fin && <Th right label="Resultado" hint="Preço de venda menos custo, comissões e impostos. “parcial” quando faltam fatores; “—” quando não há custo utilizável." />}
            {fin && <Th right label="Margem (%)" hint="Resultado dividido pelo preço de venda. Cor: verde acima da meta, neutro entre, vermelho abaixo da mínima." />}
            <Th right label="Novo preço" hint={readOnly ? "Preço do arquivo importado para esta linha. Vazio mantém o preço atual." : "Digite um preço para simular. Vazio mantém o preço atual. A simulação recalcula no backend ao clicar em “Simular alterações”."} cls="bg-yellow-100" />
            {fin && <Th right label="Nova margem (%)" hint="Margem projetada com o preço simulado. Calculada pelo backend sobre o preço de venda resultante (após piso), nunca sobre o valor digitado." cls="bg-primary/5" />}
            <Th right label="Redução ou acréscimo (%)" hint="Variação do preço de venda em relação ao atual. “+” aumento (verde), negativo redução (vermelho)." cls="bg-primary/5" />
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <SimRow
              key={rowKey(r)}
              row={r}
              fin={fin}
              editable={editable}
              dirty={!!dirty}
              readOnly={!!readOnly}
              value={inputs?.[rowKey(r)] ?? ""}
              onChange={onInput ? (val) => onInput(rowKey(r), val) : undefined}
            />
          ))}
          {data.rows.length === 0 && (
            <tr><td colSpan={colCount} className="py-6 text-center text-muted-foreground">Nenhuma linha nesta versão.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Cabeçalho de contexto da versão (cliente/unidade/negociação/vigência/versão/data). */
function SimContextHeader({ data }: { data: SimulationResponse }) {
  return (
    <div className="mb-2 grid gap-x-4 gap-y-0.5 text-[11px] sm:grid-cols-2 lg:grid-cols-3">
      <span>Cliente: <strong>{data.customerName}</strong></span>
      <span>Unidade de entrega: <strong>{data.unitName ?? "— (nível do cliente)"}</strong></span>
      <span>Negociação: <strong>{scopeLabel(data.negotiationScope)}</strong></span>
      <span>Vigência: <strong>{brDate(data.validFrom)} → {data.validTo ? brDate(data.validTo) : "em aberto"}</strong></span>
      <span>Versão/base: <strong>v{data.versionNumber}</strong> · {statusLabel[data.versionStatus] ?? data.versionStatus} · origem {data.source}</span>
      <span>Data de referência: <strong>{brDate(data.referenceDate)}</strong></span>
    </div>
  );
}

/**
 * Visão de simulação de 16 colunas da versão. Mostra a rentabilidade ATUAL de cada linha e permite digitar
 * um "novo preço" para simular a margem/variação projetada. TODOS os valores vêm do backend — o FE só ecoa
 * o preço digitado e renderiza. Colunas financeiras só aparecem quando `financialVisible`.
 */
function SimulationView({
  conditionId,
  versionId,
  onApplied,
}: {
  conditionId: string;
  versionId: string;
  onApplied: () => Promise<void>;
}) {
  const [data, setData] = useState<SimulationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Preços digitados por linha (texto cru do input). dirty = houve edição desde a última simulação.
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);
  const [page, setPage] = useState(0);

  const loadSim = useCallback(async (toPage: number, overrides: SimOverride[]) => {
    setSimulating(true);
    setError(null);
    try {
      const r = await simulateVersion(conditionId, versionId, { overrides, page: toPage, size: SIM_SIZE });
      setData(r);
      setPage(r.page);
      setDirty(false);
      return r;
    } catch (e) {
      setError(getApiErrorMessage(e, "Falha ao simular a rentabilidade."));
      return null;
    } finally {
      setSimulating(false);
      setLoading(false);
    }
  }, [conditionId, versionId]);

  useEffect(() => { void loadSim(0, []); }, [loadSim]);

  // Overrides = toda linha cujo input tenha um número > 0. cutName vem da linha correspondente (data.rows).
  const buildOverrides = useCallback((): SimOverride[] => {
    const byKey = new Map<string, SimulationRow>();
    (data?.rows ?? []).forEach((r) => byKey.set(rowKey(r), r));
    const out: SimOverride[] = [];
    for (const [key, raw] of Object.entries(inputs)) {
      const price = Number(raw.replace(",", "."));
      if (!raw.trim() || !Number.isFinite(price) || price <= 0) continue;
      const row = byKey.get(key);
      if (!row) continue;
      out.push({ productId: row.productId, unitId: row.unitId, cutName: row.cutName, price });
    }
    return out;
  }, [data, inputs]);

  const onSimulate = useCallback(() => { void loadSim(page, buildOverrides()); }, [loadSim, page, buildOverrides]);

  const onApply = useCallback(async () => {
    if (!data) return;
    setApplying(true);
    setError(null);
    try {
      const r = await applySimulation(conditionId, versionId, {
        overrides: buildOverrides(),
        expectedVersion: data.expectedVersion,
        baseFingerprint: data.baseFingerprint,
        page: data.page,
        size: SIM_SIZE,
      });
      setData(r);
      setPage(r.page);
      setInputs({});
      setDirty(false);
      toast.success("Novos preços aplicados ao rascunho.");
      await onApplied();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError(`${getApiErrorMessage(e, "A base da versão mudou.")} Clique em “Simular alterações” novamente antes de aplicar.`);
      } else {
        setError(getApiErrorMessage(e, "Não foi possível aplicar ao rascunho."));
      }
    } finally {
      setApplying(false);
      setConfirmOpen(false);
    }
  }, [data, conditionId, versionId, buildOverrides, onApplied]);

  const setInput = (key: string, value: string) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  if (loading && !data) {
    return (
      <div className="mt-2 flex items-center gap-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Carregando rentabilidade…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mt-2 border-t border-border/60 pt-2 text-xs text-destructive">
        {error ?? "Falha ao carregar a rentabilidade."}
      </div>
    );
  }

  const fin = data.financialVisible;
  const editable = data.editable;
  const totalPages = Math.max(1, Math.ceil(data.totalRows / Math.max(1, data.size)));

  return (
    <div className="mt-3 border-t border-border/60 pt-2">
      <SimContextHeader data={data} />

      {/* Barra de ações da simulação */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" disabled={simulating || applying} onClick={onSimulate}>
          {simulating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Simular alterações
        </Button>
        {editable && (
          <Button size="sm" disabled={simulating || applying || dirty} onClick={() => setConfirmOpen(true)}>
            {applying ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
            Aplicar ao rascunho
          </Button>
        )}
        {dirty && (
          <span className="text-[11px] font-medium text-warning">
            Simulação desatualizada — clique em “Simular alterações”.
          </span>
        )}
        {!fin && (
          <span className="text-[11px] text-muted-foreground">Valores financeiros ocultos (sem permissão).</span>
        )}
      </div>

      {error && (
        <div className="mb-2 rounded border border-destructive/40 bg-destructive/5 p-1.5 text-[11px] text-destructive">
          {error}
        </div>
      )}

      <SimTable data={data} inputs={inputs} dirty={dirty} onInput={setInput} />

      {/* Paginação */}
      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{data.totalRows} linha(s) · página {data.page + 1} de {totalPages}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={data.page <= 0 || simulating}
            onClick={() => void loadSim(data.page - 1, buildOverrides())}>Anterior</Button>
          <Button size="sm" variant="outline" disabled={data.page + 1 >= totalPages || simulating}
            onClick={() => void loadSim(data.page + 1, buildOverrides())}>Próxima</Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aplicar os novos preços ao rascunho?</AlertDialogTitle>
            <AlertDialogDescription>
              Os preços digitados serão gravados como preços próprios desta versão (rascunho). Esta ação
              altera a versão — mas não a tabela vigente. Confirme apenas após conferir a simulação.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancelar</AlertDialogCancel>
            <AlertDialogAction disabled={applying} onClick={(e) => { e.preventDefault(); void onApply(); }}>
              {applying ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Aplicar ao rascunho
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const SIM_SIZE = 50;

// Detalhe do piso: quando o piso impede o preço digitado de ser o preço final, mostra o preço de venda
// RESULTANTE. Nunca apresenta margem calculada sobre o valor digitado (essa vem do backend, já sobre o
// preço resultante).
function floorDetail(row: SimulationRow): string | null {
  const sim = row.simulated;
  if (!sim) return null;
  const floored = sim.priceFloorApplied || (row.newPrice != null && sim.salePrice != null && sim.salePrice !== row.newPrice);
  if (!floored || sim.salePrice == null) return null;
  return `Preço de venda resultante: ${money(sim.salePrice)} (piso aplicado)`;
}

function SimRow({
  row, fin, editable, dirty, readOnly, value, onChange,
}: {
  row: SimulationRow;
  fin: boolean;
  editable: boolean;
  dirty: boolean;
  readOnly?: boolean;
  value: string;
  onChange?: (v: string) => void;
}) {
  const cur = row.current;
  const sim = row.simulated;
  // Colunas simuladas (15/16) só são válidas se há simulado E a simulação não está desatualizada.
  const simValid = !dirty && sim != null;
  const floorMsg = floorDetail(row);
  const curMargin = cur.marginPercent ?? cur.partialMarginPercent;
  const simMargin = sim ? (sim.marginPercent ?? sim.partialMarginPercent) : null;

  return (
    <tr className="border-t border-border/40 align-top">
      <td className="py-1 px-1.5 tabular-nums">{cur.code ?? "—"}</td>
      <td className="py-1 px-1.5">{row.productName}</td>
      <td className="py-1 px-1.5">{row.unitName}</td>
      <td className="py-1 px-1.5">{row.cutName ?? "base"}</td>

      {fin && (
        <td className="py-1 px-1.5 text-right tabular-nums">
          {cur.costValue == null ? (
            <span className="text-muted-foreground" title={costBreakdown(cur) || undefined}>—</span>
          ) : (
            <span title={costBreakdown(cur) || undefined} className={cn(!cur.costComplete && "cursor-help underline decoration-dotted")}>
              {money(cur.costValue)}
              {!cur.costComplete && <span className="ml-1 text-[9px] text-muted-foreground">parcial</span>}
            </span>
          )}
        </td>
      )}
      {fin && <td className="py-1 px-1.5 text-right tabular-nums">{money(cur.clientCommissionValue)}</td>}
      {fin && <td className="py-1 px-1.5 text-right tabular-nums">{money(cur.intermediaryCommissionValue)}</td>}
      {fin && <td className="py-1 px-1.5 text-right tabular-nums">{money(cur.taxesUnitValue)}</td>}

      <td className="py-1 px-1.5 text-right tabular-nums">{money(cur.registeredPrice)}</td>
      <td className="py-1 px-1.5">
        {cur.priceFloorApplied ? (
          <span className="rounded bg-warning/15 px-1 text-[9px] font-bold uppercase text-warning">Piso aplicado</span>
        ) : "—"}
      </td>
      <td className="py-1 px-1.5 text-right tabular-nums">{money(cur.salePrice)}</td>

      {fin && (
        <td className="py-1 px-1.5 text-right tabular-nums">
          {money(cur.resultValue ?? cur.partialResultValue)}
          {cur.partial && <span className="ml-1 text-[9px] text-muted-foreground">parcial</span>}
        </td>
      )}
      {fin && (
        <td className={cn("py-1 px-1.5 text-right tabular-nums font-medium", cur.marginStatus ? marginStatusStyle[cur.marginStatus] : "")}>
          {marginPct(curMargin)}
          {cur.partial && <span className="ml-1 text-[9px] font-normal text-muted-foreground">parcial</span>}
        </td>
      )}

      {/* 14 — Novo preço (editável ao vivo; só leitura na prévia de importação) */}
      <td className="bg-yellow-100 py-1 px-1.5 text-right">
        {readOnly ? (
          <span className={cn("tabular-nums", row.priceOverridden ? "font-semibold" : "text-muted-foreground")}>
            {money(row.newPrice)}
          </span>
        ) : (
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={value}
            disabled={!editable}
            placeholder="—"
            onChange={(e) => onChange?.(e.target.value)}
            aria-label={`Novo preço de ${row.productName} ${row.unitName} ${row.cutName ?? "base"}`}
            className="ml-auto h-7 w-24 text-right text-[11px] tabular-nums"
          />
        )}
        {simValid && floorMsg && (
          <div className="mt-0.5 text-[9px] font-normal text-warning">{floorMsg}</div>
        )}
      </td>

      {/* 15 — Nova margem (%) */}
      {fin && (
        <td className={cn("py-1 px-1.5 text-right tabular-nums", simValid ? (sim?.marginStatus ? marginStatusStyle[sim.marginStatus] : "") : "text-muted-foreground")}>
          {simValid ? (
            <>
              {marginPct(simMargin)}
              {sim?.partial && <span className="ml-1 text-[9px] text-muted-foreground">parcial</span>}
            </>
          ) : dirty && sim ? <span title="Simulação desatualizada">…</span> : "—"}
        </td>
      )}

      {/* 16 — Redução ou acréscimo (%) */}
      <td className={cn("py-1 px-1.5 text-right tabular-nums",
        simValid && row.changePercent != null
          ? (row.changePercent > 0 ? "text-primary" : row.changePercent < 0 ? "text-destructive" : "")
          : "text-muted-foreground")}>
        {simValid ? changePct(row.changePercent) : dirty && sim ? <span title="Simulação desatualizada">…</span> : "—"}
      </td>
    </tr>
  );
}
